import { useState, useEffect } from "react";
import { Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useMoods, MOOD_EMOJIS } from "@/hooks/useMoods";
import { cn } from "@/lib/utils";

export function MoodSelector() {
  const { myMood, setMood, canSetMood, timeUntilNextSet, isSettingMood } = useMoods();
  const [isOpen, setIsOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Update countdown timer
  useEffect(() => {
    if (timeUntilNextSet > 0) {
      setCountdown(Math.ceil(timeUntilNextSet / 1000));
      const interval = setInterval(() => {
        setCountdown((prev) => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCountdown(0);
    }
  }, [timeUntilNextSet]);

  const handleSelect = (emoji: string) => {
    setMood(emoji);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className="px-2.5 shadow-sm ring-1 ring-border"
          aria-label="Set your mood"
        >
          {myMood ? (
            <span className="text-base leading-none">{myMood.emoji}</span>
          ) : (
            <Smile className="h-5 w-5" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground px-1 pb-1.5">
          How are you feeling?
        </div>
        <div className="grid grid-cols-5 gap-1">
          {MOOD_EMOJIS.map(({ emoji, label }) => (
            <button
              key={emoji}
              onClick={() => handleSelect(emoji)}
              disabled={!canSetMood || isSettingMood}
              title={label}
              className={cn(
                "p-1.5 text-xl rounded-md transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/50",
                myMood?.emoji === emoji && "bg-primary/10 ring-1 ring-primary/30",
                (!canSetMood || isSettingMood) && "opacity-50 cursor-not-allowed"
              )}
            >
              {emoji}
            </button>
          ))}
        </div>
        {!canSetMood && countdown > 0 && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            Wait {countdown}s to change
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
